import React, { useEffect, useMemo, useState } from "react";
import { ChevronRight, Info } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as dashboardActions from "src/redux/slices/dashboardSlice";
import * as tenantActions from "src/redux/slices/tenantSlice";
import * as transactionActions from "src/redux/slices/transactionSlice";
import Card from "src/components/common/card/Card";
import CardHeader from "src/components/common/card/CardHeader";
import TableV2 from "src/components/ui/TableV2";
import { Badge } from "src/components/ui/Badge";
import Dropdown from "src/components/ui/Dropdown";
import { currencyFormatter } from "src/utils/common";
import { formatDateTime } from "src/utils/date-time";
import { TenantPlanSnapshot } from "src/types/tenant.types";
import AreaChartWrapper from "src/components/ui/chart/AreaChartWrapper";
import Popover from "src/components/ui/Popover";

const getActivePlan = (plans: TenantPlanSnapshot[] | undefined) => {
  if (!plans?.length) return null;
  const now = new Date();
  const sortedPlans = [...plans].sort((a, b) => {
    const aDate = a.planStartsAt || a.createdAt || "";
    const bDate = b.planStartsAt || b.createdAt || "";
    return new Date(bDate).getTime() - new Date(aDate).getTime();
  });
  return (
    sortedPlans.find((plan) =>
      plan.planEndsAt ? new Date(plan.planEndsAt) >= now : false
    ) || sortedPlans[0]
  );
};

const getPlanStatus = (planEndsAt?: string) => {
  if (!planEndsAt) return "active";
  return new Date(planEndsAt) < new Date() ? "expired" : "active";
};

const getDaysLeft = (planEndsAt?: string) => {
  if (!planEndsAt) return null;
  const diff = new Date(planEndsAt).getTime() - new Date().getTime();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
};

const PLAN_USAGE_COLORS = [
  "#6366F1",
  "#14B8A6",
  "#F59E0B",
  "#EF4444",
  "#3B82F6",
  "#8B5CF6",
  "#10B981",
];

const PLAN_USAGE_PERIOD_OPTIONS = [
  { label: "3 months", value: "3" },
  { label: "6 months", value: "6" },
  { label: "12 months", value: "12" },
];

const SuperAdminDashboard: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { superAdminMetric, superAdminPlanUsage } = useAppSelector(
    (state) => state.dashboard
  );
  const { tenantList } = useAppSelector((state) => state.tenant);
  const { transactionsList } = useAppSelector((state) => state.transaction);
  const [planUsageMonths, setPlanUsageMonths] = useState("6");

  useEffect(() => {
    dispatch(dashboardActions.getSuperAdminDashboardMetricRequest());
    dispatch(tenantActions.getTenantsListRequest({ page: 1, limit: 200 }));
    dispatch(transactionActions.getTransactionListRequest({ page: 1, limit: 8 }));
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      dashboardActions.getSuperAdminPlanUsageRequest({
        months: parseInt(planUsageMonths, 10),
      })
    );
  }, [dispatch, planUsageMonths]);

  const tenants = tenantList?.data?.data || [];
  const metrics = superAdminMetric.data;
  const recentTransactions = transactionsList?.data?.data || [];
  const planUsageData = useMemo(() => {
    const labels = superAdminPlanUsage.data?.labels || [];
    const series = superAdminPlanUsage.data?.series || [];
    const chartData = labels.map((label, index) => {
      const point: Record<string, string | number> = { label };
      series.forEach((item) => {
        point[item.plan] = item.data[index] ?? 0;
      });
      return point;
    });
    return { labels, series, chartData };
  }, [superAdminPlanUsage.data]);

  const expiringSoon = useMemo(() => {
    return tenants
      .map((tenant) => {
        const activePlan = getActivePlan(tenant.plans);
        const daysLeft = getDaysLeft(activePlan?.planEndsAt);
        return {
          _id: tenant._id,
          tenantName: tenant.tenantName,
          planName:
            activePlan?.planName ||
            activePlan?.planCode ||
            activePlan?.planId ||
            "No Plan",
          planEndsAt: activePlan?.planEndsAt,
          daysLeft,
          status: getPlanStatus(activePlan?.planEndsAt),
        };
      })
      .filter((item) => item.daysLeft !== null && item.daysLeft <= 14 && item.daysLeft >= 0)
      .sort((a, b) => (a.daysLeft ?? 0) - (b.daysLeft ?? 0))
      .slice(0, 8);
  }, [tenants]);

  const kpiCards: {
    label: string;
    value: string | number;
    description: string;
    link?: string;
  }[] = [
    {
      label: "Total Tenants",
      value: metrics?.totalTenants ?? 0,
      description: "All tenants registered on the platform.",
      link: "/admin/tenant/list",
    },
    {
      label: "Tenants with Trial Plan",
      value: metrics?.tenantsWithTrialPlan ?? 0,
      description: "Tenants currently on a trial plan.",
      link: "/admin/tenant/list",
    },
    {
      label: "Tenants with Active Plan",
      value: metrics?.tenantsWithActivePlan ?? 0,
      description: "Tenants whose paid plan is currently valid.",
      link: "/admin/tenant/list",
    },
    {
      label: "Tenants with Plan Expired",
      value: metrics?.tenantsWithExpiredPlan ?? 0,
      description: "Tenants without an active plan.",
      link: "/admin/tenant/list",
    },
    {
      label: "New Sign-up Requests",
      value: metrics?.pendingSignupRequests ?? 0,
      description: "Self-serve sign-ups waiting on your review.",
      link: "/admin/signup-requests",
    },
    {
      label: "Open Support Requests",
      value: metrics?.openSupportRequests ?? 0,
      description: "Support tickets that haven't been closed yet.",
      link: "/admin/support",
    },
    {
      label: "Total Revenue",
      value: currencyFormatter(metrics?.totalRevenue || 0),
      description: "Sum of successful platform transactions.",
      link: "/admin/reports",
    },
    {
      label: "Total Transactions",
      value: metrics?.totalTransactions ?? 0,
      description: "All payment/refund transactions recorded.",
      link: "/admin/transaction/list",
    },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        {kpiCards.map((card) => {
          const Wrapper: any = card.link ? "button" : "div";
          return (
            <Wrapper
              key={card.label}
              type={card.link ? "button" : undefined}
              onClick={card.link ? () => navigate(card.link as string) : undefined}
              className={`group rounded-xl border border-gray-200 bg-white p-4 text-left shadow-sm dark:border-gray-700 dark:bg-gray-800 ${
                card.link
                  ? "cursor-pointer transition hover:-translate-y-0.5 hover:border-primary-200 hover:shadow-md dark:hover:border-primary-800"
                  : ""
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm text-gray-500 dark:text-gray-400">{card.label}</p>
                {card.description && (
                  <Popover
                    openOnHover
                    trigger={
                      <span className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300">
                        <Info size={14} />
                      </span>
                    }
                  >
                    <div className="max-w-xs px-3 py-2 text-xs text-gray-600">
                      {card.description}
                    </div>
                  </Popover>
                )}
              </div>
              <p className="mt-2 text-2xl font-semibold text-gray-900 dark:text-gray-100">
                {card.value}
              </p>
              <div className="mt-4 flex items-center justify-between">
                <div className="h-1 w-12 rounded-full bg-primary-200 dark:bg-primary-800" />
                {card.link && (
                  <ChevronRight
                    size={16}
                    className="text-gray-300 transition group-hover:translate-x-0.5 group-hover:text-primary-500 dark:text-gray-600"
                  />
                )}
              </div>
            </Wrapper>
          );
        })}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <Card>
          <CardHeader
            title="Plan Usage"
            description="Active tenants by plan over time"
            actions={
              <Dropdown
                value={planUsageMonths}
                options={PLAN_USAGE_PERIOD_OPTIONS}
                onChange={setPlanUsageMonths}
              />
            }
          />
          {superAdminPlanUsage.loading ? (
            <p className="text-sm text-gray-500 mt-4">Loading plan usage...</p>
          ) : superAdminPlanUsage.data?.series?.length ? (
            <AreaChartWrapper
              data={planUsageData.chartData}
              xKey="label"
              areas={planUsageData.series.map((item, index) => ({
                dataKey: item.plan,
                stroke: PLAN_USAGE_COLORS[index % PLAN_USAGE_COLORS.length],
                fillOpacity: 0.12,
                strokeWidth: 2,
                dot: false,
                type: "monotone",
              }))}
              showLegend
              hideXAxis={false}
              hideYAxis={false}
              xAxisProps={{ tick: { fontSize: 11 } }}
              yAxisProps={{ allowDecimals: false, tick: { fontSize: 11 } }}
              legendProps={{
                verticalAlign: "top",
                align: "left",
                wrapperStyle: { fontSize: "12px" },
              }}
            />
          ) : (
            <p className="text-sm text-gray-500 mt-4">No plan usage data.</p>
          )}
        </Card>

        <Card>
          <CardHeader
            title="Expiring Soon"
            description="Plans ending in the next 14 days"
          />
          <TableV2
            columns={[
              {
                key: "tenantName",
                header: "Tenant",
                accessor: ({ row }) => (
                  <button
                    className="font-medium text-primary-600 hover:underline dark:text-primary-400"
                    onClick={() => navigate(`/admin/tenant/${row._id}/details`)}
                  >
                    {row.tenantName}
                  </button>
                ),
              },
              { key: "planName", header: "Plan" },
              {
                key: "daysLeft",
                header: "Days Left",
                align: "right",
                accessor: ({ row }) => (
                  <span className="font-medium">{row.daysLeft}</span>
                ),
              },
              {
                key: "planEndsAt",
                header: "Ends On",
                accessor: ({ row }) =>
                  formatDateTime(row.planEndsAt, "DD MMM YYYY"),
              },
            ]}
            data={expiringSoon}
          />
        </Card>

        <Card>
          <CardHeader
            title="Recent Transactions"
            description="Latest transactions across the platform"
          />
          <TableV2
            columns={[
              {
                key: "transactionId",
                header: "Transaction",
                accessor: ({ row }) => (
                  <button
                    className="font-mono text-xs text-primary-600 hover:underline dark:text-primary-400"
                    onClick={() =>
                      navigate(`/admin/transaction/${row._id}/details`)
                    }
                  >
                    {row?.transactionId || row?._id}
                  </button>
                ),
              },
              {
                key: "amount",
                header: "Amount",
                align: "right",
                accessor: ({ row }) => currencyFormatter(row?.amount || 0),
              },
              {
                key: "status",
                header: "Status",
                accessor: ({ row }) => {
                  const status = row?.status || "";
                  const color =
                    status === "success"
                      ? "green"
                      : status === "failed"
                      ? "red"
                      : "yellow";
                  return <Badge text={status} color={color} />;
                },
              },
              {
                key: "createdAt",
                header: "Created",
                accessor: ({ row }) =>
                  formatDateTime(row.createdAt, "DD MMM YYYY"),
              },
            ]}
            data={recentTransactions}
          />
        </Card>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
